import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Slide,
} from "@mui/material";
import {
  CheckCircleRounded as SuccessIcon,
  ErrorRounded as ErrorIcon,
  WarningAmberRounded as WarningIcon,
  InfoRounded as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { borderRadiusPx } from "../../../../theme";
import {
  defaultToastConfig,
  currentToastConfig,
  getSeverityStyles,
  getSlideDirection,
  TOAST_POSITIONS,
  TOAST_THEMES,
} from "./toastConfig";

/**
 * Event emitter for managing imperative toast triggers
 */
class ToastEventEmitter {
  constructor() {
    this.listeners = new Set();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  emit(event, data) {
    this.listeners.forEach((listener) => {
      try {
        listener(event, data);
      } catch (err) {
        console.error("Toast listener error:", err);
      }
    });
  }
}

export const toastEvents = new ToastEventEmitter();

let toastIdCounter = 0;

/**
 * Normalize message parameter
 */
const normalizeMessage = (content) => {
  if (content === null || content === undefined) {
    return "";
  }
  if (content instanceof Error) {
    return content.message || "An unexpected error occurred.";
  }
  if (typeof content === "object" && !React.isValidElement(content)) {
    return content.message || content.title || JSON.stringify(content);
  }
  return content;
};

/**
 * Imperative toast function
 */
export const toast = (message, options = {}) => {
  const id = options.id || options.toastId || `toast-${++toastIdCounter}-${Date.now()}`;
  const toastData = {
    id,
    message: normalizeMessage(message),
    severity: options.severity || "info",
    ...options,
  };
  toastEvents.emit("add", toastData);
  return id;
};

toast.success = (message, options = {}) =>
  toast(message, { ...options, severity: "success" });

toast.error = (message, options = {}) =>
  toast(message, { ...options, severity: "error" });

toast.info = (message, options = {}) =>
  toast(message, { ...options, severity: "info" });

toast.warning = (message, options = {}) =>
  toast(message, { ...options, severity: "warning" });

toast.warn = (message, options = {}) =>
  toast(message, { ...options, severity: "warning" });

toast.dismiss = (id) => {
  if (id) {
    toastEvents.emit("dismiss", id);
  } else {
    toastEvents.emit("dismissAll");
  }
};

toast.clear = () => {
  toastEvents.emit("dismissAll");
};

toast.clearWaitingQueue = () => {
  toastEvents.emit("dismissAll");
};

toast.update = (id, options = {}) => {
  toastEvents.emit("update", { id, ...options });
};

toast.promise = async (promise, { pending = "Processing...", success = "Completed successfully!", error = "Something went wrong" }, options = {}) => {
  const id = toast(pending, { ...options, autoClose: false, severity: "info" });
  try {
    const res = await (typeof promise === "function" ? promise() : promise);
    const successMsg = typeof success === "function" ? success(res) : success;
    toast.update(id, {
      message: normalizeMessage(successMsg),
      severity: "success",
      autoClose: options.autoClose || currentToastConfig.autoClose,
    });
    return res;
  } catch (err) {
    const errorMsg = typeof error === "function" ? error(err) : error;
    toast.update(id, {
      message: normalizeMessage(errorMsg || err),
      severity: "error",
      autoClose: options.autoClose || currentToastConfig.autoClose,
    });
    throw err;
  }
};

/**
 * Individual Toast Item Component
 */
const ToastItem = React.memo(({ toast: item, onDismiss }) => {
  const [isPaused, setIsPaused] = useState(false);
  const remainingTimeRef = useRef(item.autoClose !== undefined ? item.autoClose : currentToastConfig.autoClose);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef(null);

  const duration = item.autoClose !== undefined ? item.autoClose : currentToastConfig.autoClose;
  const isAutoClose = duration !== false && duration > 0;
  const hideProgressBar = Boolean(item.hideProgressBar);
  const pauseOnHover = item.pauseOnHover !== false;
  const closeOnClick = Boolean(item.closeOnClick);
  const themeMode = item.theme || currentToastConfig.theme || TOAST_THEMES.COLORED;
  const variant = item.variant || currentToastConfig.variant || "filled";

  const styles = getSeverityStyles(item.severity, themeMode);
  const slideDirection = getSlideDirection(item.position || currentToastConfig.position);

  const startTimer = useCallback(() => {
    if (!isAutoClose) return;
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismiss(item.id);
    }, remainingTimeRef.current);
  }, [isAutoClose, item.id, onDismiss]);

  const pauseTimer = useCallback(() => {
    if (!isAutoClose || !pauseOnHover) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    setIsPaused(true);
  }, [isAutoClose, pauseOnHover]);

  const resumeTimer = useCallback(() => {
    if (!isAutoClose || !pauseOnHover) return;
    setIsPaused(false);
    startTimer();
  }, [isAutoClose, pauseOnHover, startTimer]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [startTimer]);

  const getSeverityIcon = () => {
    if (item.icon !== undefined) {
      return item.icon;
    }
    const iconSx = { fontSize: 22, color: themeMode === "colored" ? "#ffffff" : styles.mainColor };
    switch (item.severity) {
      case "success":
        return <SuccessIcon sx={iconSx} />;
      case "error":
        return <ErrorIcon sx={iconSx} />;
      case "warning":
      case "warn":
        return <WarningIcon sx={iconSx} />;
      case "info":
      default:
        return <InfoIcon sx={iconSx} />;
    }
  };

  return (
    <Slide in={Boolean(item.open !== false)} direction={slideDirection} mountOnEnter unmountOnExit>
      <Box
        onMouseEnter={pauseTimer}
        onMouseLeave={resumeTimer}
        onClick={closeOnClick ? () => onDismiss(item.id) : undefined}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: borderRadiusPx.lg,
          boxShadow: styles.glow,
          border: `1px solid ${styles.borderColor}`,
          backgroundColor: styles.bgColor,
          color: styles.textColor,
          backdropFilter: themeMode === "dark" ? "blur(12px)" : "none",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          cursor: closeOnClick ? "pointer" : "default",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        }}
      >
        <Alert
          severity={item.severity === "warn" ? "warning" : item.severity || "info"}
          variant={variant}
          icon={getSeverityIcon()}
          action={
            item.action || (
              <IconButton
                size="small"
                aria-label="close"
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(item.id);
                }}
                sx={{
                  color: "inherit",
                  opacity: 0.75,
                  p: 0.5,
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.12)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }
          sx={{
            backgroundColor: "transparent !important",
            color: "inherit !important",
            p: 1.5,
            pr: 1.75,
            alignItems: "center",
            "& .MuiAlert-icon": {
              mr: 1.5,
              opacity: 1,
              p: 0,
            },
            "& .MuiAlert-message": {
              p: 0,
              width: "100%",
              overflow: "hidden",
            },
            "& .MuiAlert-action": {
              p: 0,
              mr: 0,
              ml: 1.5,
              alignItems: "center",
            },
          }}
        >
          {item.title && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "0.925rem",
                lineHeight: 1.3,
                mb: item.message ? 0.35 : 0,
                color: "inherit",
              }}
            >
              {item.title}
            </Typography>
          )}
          {item.message && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.4,
                wordBreak: "break-word",
                color: "inherit",
              }}
            >
              {item.message}
            </Typography>
          )}
        </Alert>

        {/* Animated Countdown Progress Bar */}
        {isAutoClose && !hideProgressBar && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: "rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: "100%",
                backgroundColor: styles.progressColor,
                transformOrigin: "left",
                animation: `muiToastProgress ${duration}ms linear forwards`,
                animationPlayState: isPaused ? "paused" : "running",
                "@keyframes muiToastProgress": {
                  "0%": { transform: "scaleX(1)" },
                  "100%": { transform: "scaleX(0)" },
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Slide>
  );
});

ToastItem.displayName = "ToastItem";

let defaultContainers = new Set();
let activeDefaultContainerId = null;
let containerCounter = 0;

/**
 * ToastContainer Component
 * Renders stacked notifications grouped by position
 */
export const ToastContainer = ({
  position = defaultToastConfig.position,
  autoClose = defaultToastConfig.autoClose,
  hideProgressBar = defaultToastConfig.hideProgressBar,
  pauseOnHover = defaultToastConfig.pauseOnHover,
  closeOnClick = defaultToastConfig.closeOnClick,
  theme = defaultToastConfig.theme,
  variant = defaultToastConfig.variant,
  maxToasts = defaultToastConfig.maxToasts,
  limit,
  containerId,
}) => {
  const effectiveLimit = limit || maxToasts;
  const [toasts, setToasts] = useState([]);
  const containerRef = useRef(null);
  const instanceIdRef = useRef(containerId || `container-${++containerCounter}`);

  // Singleton guard for default containers to avoid duplicate renders
  const isDedicated = Boolean(containerId);

  useEffect(() => {
    const instanceId = instanceIdRef.current;
    if (!isDedicated) {
      defaultContainers.add(instanceId);
      if (!activeDefaultContainerId) {
        activeDefaultContainerId = instanceId;
      }
    }
    return () => {
      if (!isDedicated) {
        defaultContainers.delete(instanceId);
        if (activeDefaultContainerId === instanceId) {
          activeDefaultContainerId = defaultContainers.values().next().value || null;
        }
      }
    };
  }, [isDedicated]);

  const handleDismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const unsubscribe = toastEvents.subscribe((event, data) => {
      // Check container routing
      if (isDedicated && data?.containerId !== containerId) {
        return;
      }
      if (!isDedicated && data?.containerId) {
        return;
      }
      if (!isDedicated && activeDefaultContainerId && activeDefaultContainerId !== instanceIdRef.current) {
        return;
      }

      if (event === "add") {
        setToasts((prev) => {
          const exists = prev.some((t) => t.id === data.id);
          const newToast = {
            open: true,
            position,
            autoClose,
            hideProgressBar,
            pauseOnHover,
            closeOnClick,
            theme,
            variant,
            ...data,
          };
          if (exists) {
            return prev.map((t) => (t.id === data.id ? { ...t, ...newToast } : t));
          }
          const updated = [...prev, newToast];
          if (updated.length > effectiveLimit) {
            return updated.slice(updated.length - effectiveLimit);
          }
          return updated;
        });
      } else if (event === "dismiss") {
        setToasts((prev) => prev.filter((t) => t.id !== data));
      } else if (event === "dismissAll") {
        setToasts([]);
      } else if (event === "update") {
        setToasts((prev) =>
          prev.map((t) => (t.id === data.id ? { ...t, ...data } : t))
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [
    isDedicated,
    containerId,
    position,
    autoClose,
    hideProgressBar,
    pauseOnHover,
    closeOnClick,
    theme,
    variant,
    effectiveLimit,
  ]);

  if (toasts.length === 0) {
    return null;
  }

  // Group active toasts by position
  const groupedToasts = toasts.reduce((acc, t) => {
    const pos = t.position || position || TOAST_POSITIONS.TOP_RIGHT;
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(t);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(groupedToasts).map(([pos, items]) => {
        const isTop = pos.includes("top");
        const isBottom = pos.includes("bottom");
        const isLeft = pos.includes("left");
        const isRight = pos.includes("right");
        const isCenter = pos.includes("center");

        return (
          <Box
            key={pos}
            ref={containerRef}
            sx={{
              position: "fixed",
              zIndex: currentToastConfig.zIndex,
              pointerEvents: "none",
              top: isTop ? { xs: 16, sm: 24 } : "auto",
              bottom: isBottom ? { xs: 16, sm: 24 } : "auto",
              left: isLeft ? { xs: 16, sm: 24 } : isCenter ? "50%" : "auto",
              right: isRight ? { xs: 16, sm: 24 } : "auto",
              transform: isCenter ? "translateX(-50%)" : "none",
              display: "flex",
              flexDirection: isBottom ? "column-reverse" : "column",
              gap: 1.25,
              maxWidth: { xs: "calc(100vw - 32px)", sm: 400 },
              width: "100%",
            }}
          >
            {items.map((item) => (
              <Box key={item.id} sx={{ pointerEvents: "auto", width: "100%" }}>
                <ToastItem toast={item} onDismiss={handleDismiss} />
              </Box>
            ))}
          </Box>
        );
      })}
    </>
  );
};

ToastContainer.propTypes = {
  position: PropTypes.string,
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  hideProgressBar: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  theme: PropTypes.string,
  variant: PropTypes.string,
  maxToasts: PropTypes.number,
  limit: PropTypes.number,
  containerId: PropTypes.string,
};

/**
 * CommonToast: Controlled / Declarative MUI Toast Component
 */
export const CommonToast = ({
  open,
  message,
  title,
  severity = "info",
  variant = "filled",
  theme = TOAST_THEMES.COLORED,
  autoClose = 3500,
  onClose,
  position = TOAST_POSITIONS.TOP_RIGHT,
  hideProgressBar = false,
  pauseOnHover = true,
  action,
  icon,
}) => {
  if (!open) return null;

  return (
    <ToastContainer
      position={position}
      autoClose={autoClose}
      theme={theme}
      variant={variant}
      hideProgressBar={hideProgressBar}
      pauseOnHover={pauseOnHover}
    />
  );
};

CommonToast.propTypes = {
  open: PropTypes.bool,
  message: PropTypes.node,
  title: PropTypes.node,
  severity: PropTypes.oneOf(["success", "error", "warning", "info", "warn"]),
  variant: PropTypes.string,
  theme: PropTypes.string,
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  onClose: PropTypes.func,
  position: PropTypes.string,
  hideProgressBar: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  action: PropTypes.node,
  icon: PropTypes.node,
};

/**
 * Custom React hook for toast notifications
 */
export const useToast = () => {
  return {
    toast,
    showToast: toast,
    success: toast.success,
    error: toast.error,
    info: toast.info,
    warning: toast.warning,
    warn: toast.warn,
    dismiss: toast.dismiss,
    clear: toast.clear,
    promise: toast.promise,
  };
};

export default toast;
